import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Activity types enum matching backend
export enum ActivityType {
  PhoneCall = 1,
  WhatsApp = 2,
  Email = 3,
  Meeting = 4,
  SystemGenerated = 5
}

// Call result enum matching backend
export enum CallResult {
  Interested_FollowUp = 1,
  Interested_ReadyToSign = 2,
  Undecided_CallBack = 3,
  NotAvailable = 4,
  NotInterested = 5,
  NoAnswer = 6,
  WrongNumber = 7,
  Other = 8
}

// Entity type enum matching backend
export enum EntityType {
  Lead = 1,
  Client = 2,
  Case = 3,
  General = 4
}

export interface ActivityDto {
  id: number;
  entityType: number;
  entityId: number;
  activityType: number;
  activityTypeName: string;
  title: string;
  description: string;
  callResult?: number | null;
  callResultName?: string | null;
  callBackTimeframe?: string | null;
  durationMinutes?: number | null;
  performedByUserId: number;
  performedByUserName: string;
  isSystemGenerated: boolean;
  relatedReminderId?: number | null;
  createdAt: Date | string;
}

export interface CreateActivityDto {
  entityType: number;
  entityId: number;
  activityType: number;
  title: string;
  description: string;
  callResult?: number | null;
  callBackTimeframe?: string | null;
  durationMinutes?: number | null;
  relatedReminderId?: number | null;
}

export interface UpdateActivityDto {
  description: string;
  durationMinutes?: number | null;
}

export interface ActivityStatisticsDto {
  totalActivities: number;
  phoneCalls: number;
  whatsAppMessages: number;
  emails: number;
  meetings: number;
  totalDurationMinutes: number;
  lastActivityDate?: Date | string | null;
  lastActivityType?: string | null;
}

// Helper functions
export const getActivityTypeName = (type: ActivityType): string => {
  switch (type) {
    case ActivityType.PhoneCall:
      return 'שיחת טלפון';
    case ActivityType.WhatsApp:
      return 'הודעת וואטסאפ';
    case ActivityType.Email:
      return 'אימייל';
    case ActivityType.Meeting:
      return 'פגישה';
    case ActivityType.SystemGenerated:
      return 'פעולת מערכת';
    default:
      return 'אחר';
  }
};

export const getCallResultName = (result: CallResult): string => {
  switch (result) {
    case CallResult.Interested_FollowUp:
      return 'מעוניין - צריך מעקב';
    case CallResult.Interested_ReadyToSign:
      return 'מעוניין - מוכן לחתום';
    case CallResult.Undecided_CallBack:
      return 'מתלבט - להתקשר בעוד...';
    case CallResult.NotAvailable:
      return 'לא זמין';
    case CallResult.NotInterested:
      return 'לא מעוניין';
    case CallResult.NoAnswer:
      return 'אין מענה';
    case CallResult.WrongNumber:
      return 'מספר שגוי';
    case CallResult.Other:
      return 'אחר';
    default:
      return 'לא ידוע';
  }
};

export const getCallResultColor = (result: CallResult): string => {
  switch (result) {
    case CallResult.Interested_FollowUp:
    case CallResult.Interested_ReadyToSign:
      return '#16a34a';
    case CallResult.Undecided_CallBack:
      return '#d97706';
    case CallResult.NotAvailable:
    case CallResult.NoAnswer:
      return 'var(--text-secondary)';
    case CallResult.NotInterested:
    case CallResult.WrongNumber:
      return '#dc2626';
    default:
      return 'var(--text-primary)';
  }
};

// API service
export const activityApi = {
  // Create a new activity
  create: async (activity: CreateActivityDto): Promise<ActivityDto> => {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_BASE_URL}/activities`, activity, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Get activity by ID
  getById: async (id: number): Promise<ActivityDto> => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/activities/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Get activities by entity
  getByEntity: async (entityType: EntityType, entityId: number): Promise<ActivityDto[]> => {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_BASE_URL}/activities/entity/${entityType}/${entityId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  // Get my recent activities
  getMyRecent: async (count: number = 10): Promise<ActivityDto[]> => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/activities/my-recent`, {
      params: { count },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Get activity statistics
  getStatistics: async (
    entityType: EntityType,
    entityId: number
  ): Promise<ActivityStatisticsDto> => {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_BASE_URL}/activities/statistics/${entityType}/${entityId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  // Update activity
  update: async (id: number, update: UpdateActivityDto): Promise<ActivityDto> => {
    const token = localStorage.getItem('token');
    const response = await axios.put(`${API_BASE_URL}/activities/${id}`, update, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // Delete activity (admin only)
  delete: async (id: number): Promise<void> => {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_BASE_URL}/activities/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },
};

// Format activity date for display
export const formatActivityDate = (date: Date | string): string => {
  const activityDate = new Date(date);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - activityDate.getTime()) / 60000);
  
  if (diffInMinutes < 1) return 'עכשיו';
  if (diffInMinutes < 60) return `לפני ${diffInMinutes} דקות`;
  if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `לפני ${hours} ${hours === 1 ? 'שעה' : 'שעות'}`;
  }
  if (diffInMinutes < 2880) return 'אתמול';
  
  const days = Math.floor(diffInMinutes / 1440);
  if (days < 7) return `לפני ${days} ימים`;
  
  return activityDate.toLocaleDateString('he-IL');
};

// Format duration for display
export const formatDuration = (minutes?: number | null): string => {
  if (!minutes) return '';
  
  if (minutes < 60) return `${minutes} דקות`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) return `${hours} ${hours === 1 ? 'שעה' : 'שעות'}`;
  
  return `${hours} ${hours === 1 ? 'שעה' : 'שעות'} ו-${remainingMinutes} דקות`;
};

// Handle API errors
export const handleActivityApiError = (error: any): string => {
  if (error.response?.data) {
    return typeof error.response.data === 'string' 
      ? error.response.data 
      : error.response.data.message || 'שגיאה לא ידועה';
  }
  return error.message || 'שגיאת תקשורת עם השרת';
};

export default activityApi;